import React, { Component } from "react";
import ReactApexChart from "react-apexcharts";
import section1DummyData from "./section1DummyData";
import "./_section1.scss";
import { Container, Modal } from "react-bootstrap";

/* ---------------------------------------------------------------- helpers */

/* Mastery / accuracy colour coding: 🟢 85+  🔵 70-84  🟡 55-69  🔴 below 55 */
const bandFor = (value) => {
    if (value >= 85) return "excellent";
    if (value >= 70) return "good";
    if (value >= 55) return "average";
    return "poor";
};

/* Mirrors the foundation theme tokens (--fdn-green / --fdn-blue / --fdn-orange /
   --fdn-red). Kept as hex because ApexCharts and inline styles need real colours. */
const BAND_COLOR = {
    excellent: "#22c55e",
    good: "#1a8cec",
    average: "#ff8a00",
    poor: "#ff4d4f",
};

/* Chart chrome colours — mirror --fdn-muted / --fdn-line / --fdn-bg. */
const CHART_FG = "#8a88a6";
const CHART_GRID = "#ebe9f7";
const CHART_TRACK = "#f0eefb";

/* Presentational: icon + tone shown next to each section title. */
const SECTION_LOOK = {
    "quick-actions": { icon: "fas fa-bolt", tone: "orange" },
    kpis: { icon: "fas fa-chart-pie", tone: "violet" },
    insights: { icon: "fas fa-lightbulb", tone: "teal" },
    exams: { icon: "fas fa-clipboard-list", tone: "blue" },
    "planner-board": { icon: "fas fa-calendar-alt", tone: "pink" },
    attendance: { icon: "fas fa-user-check", tone: "green" },
};

/* Presentational: subject name -> theme subject class (fdn-subj--*). */
const subjClass = (name) => `fdn-subj--${String(name || "").toLowerCase().replace(/[^a-z]/g, "")}`;

/* Small animated counter — kept in this file so the dashboard stays 2 files. */
class CountUp extends Component {
    constructor(props) {
        super(props);
        this.state = { display: 0 };
    }

    componentDidMount() {
        const { value, duration = 1100 } = this.props;
        const start = performance.now();
        const tick = (now) => {
            const progress = Math.min((now - start) / duration, 1);
            this.setState({ display: value * (1 - Math.pow(1 - progress, 3)) });
            if (progress < 1) this.frame = requestAnimationFrame(tick);
        };
        this.frame = requestAnimationFrame(tick);
    }

    componentWillUnmount() {
        if (this.frame) cancelAnimationFrame(this.frame);
    }

    render() {
        const { decimals = 0, prefix = "", suffix = "" } = this.props;
        const shown = Number(this.state.display.toFixed(decimals));
        return (
            <React.Fragment>
                {prefix}
                {shown.toLocaleString("en-IN", {
                    minimumFractionDigits: decimals,
                    maximumFractionDigits: decimals,
                })}
                {suffix}
            </React.Fragment>
        );
    }
}

/**
 * IIT / NEET Foundation Student Dashboard.
 *
 * One component, one data file. Every value is read from the `data` prop
 * (dummy JSON is only the fallback), so swapping in an API response needs no
 * markup changes.
 */
class IITFoundationStudentDashboardSection1 extends Component {
    constructor(props) {
        super(props);
        this.state = {
            photoFailed: false,
            mounted: false,   // drives the progress-bar fill animation
            loading: true,    // shows skeletons on first paint
            collapsed: {},    // section id -> bool (collapsible on mobile)
            analysisExam: props.initialAnalysisExam || null, // exam whose analysis modal is open
            questionFilter: "all", // all | correct | wrong | unattempted
        };
    }

    componentDidMount() {
        this.mountTimer = setTimeout(() => this.setState({ mounted: true }), 140);
        this.loadTimer = setTimeout(() => this.setState({ loading: false }), 650);
    }

    componentWillUnmount() {
        clearTimeout(this.mountTimer);
        clearTimeout(this.loadTimer);
    }

    isLoading = () =>
        this.props.loading !== undefined ? this.props.loading : this.state.loading;

    toggleSection = (id) =>
        this.setState((state) => ({
            collapsed: { ...state.collapsed, [id]: !state.collapsed[id] },
        }));

    emit = (action, payload) => {
        const { onAction } = this.props;
        if (onAction) onAction(action, payload);
    };

    /* ------------------------------------------------------------ shells */

    /** Section shell: title bar + collapse toggle + body. */
    renderSection = (config, body) => {
        const collapsed = !!this.state.collapsed[config.id];
        const look = SECTION_LOOK[config.id] || { icon: "fas fa-star", tone: "violet" };

        return (
            <section className={`iitf-sec fdn-pop ${config.className || ""}`} key={config.id}>
                <div className="iitf-sec__head">
                    <div className="iitf-sec__title">
                        <span className={`iitf-sec__icon tone-${look.tone}`} aria-hidden="true">
                            <i className={look.icon} />
                        </span>
                        <div>
                            <h4>{config.title}</h4>
                            {config.subtitle ? <p>{config.subtitle}</p> : null}
                        </div>
                    </div>

                    <div className="iitf-sec__tools">
                        {config.badge ? <span className="iitf-sec__badge fdn-badge fdn-badge--violet">{config.badge}</span> : null}
                        {config.actionLabel ? (
                            // <button
                            //     type="button"
                            //     className="iitf-sec__action"
                            //     onClick={() => this.emit(config.actionKey || config.id)}
                            // >
                            //     {config.actionIcon ? <i className={config.actionIcon} /> : null}
                            //     {config.actionLabel}
                            // </button>
                            ""
                        ) : null}
                        <button
                            type="button"
                            className="iitf-sec__toggle"
                            aria-expanded={!collapsed}
                            onClick={() => this.toggleSection(config.id)}
                        >
                            <i className={`fas fa-chevron-${collapsed ? "down" : "up"}`} />
                        </button>
                    </div>
                </div>

                <div className={`iitf-sec__body ${collapsed ? "is-collapsed" : ""}`}>{body}</div>
            </section>
        );
    };

    /** Skeleton placeholder — count blocks in a responsive row. */
    renderSkeleton = (count, modifier) => (
        <div className={`iitf-skel ${modifier || ""}`}>
            {Array.from({ length: count }).map((_, index) => (
                <div className="iitf-skel__block" key={index}>
                    <span className="iitf-skel__dot fdn-skel" />
                    <span className="line fdn-skel w40" />
                    <span className="line fdn-skel w70" />
                    <span className="line fdn-skel w55" />
                </div>
            ))}
        </div>
    );

    /* ------------------------------------------------------------- header */

    getGreeting = () => {
        const hour = new Date().getHours();
        if (hour < 12) return "Good Morning";
        if (hour < 17) return "Good Afternoon";
        return "Good Evening";
    };

    getInitials = (name) =>
        String(name || "")
            .trim()
            .split(/\s+/)
            .slice(0, 2)
            .map((word) => word.charAt(0).toUpperCase())
            .join("");

    renderTagline = (tagline, program) => {
        if (!program || tagline.indexOf(program) === -1) return tagline;
        const [before, after] = tagline.split(program);
        return (
            <React.Fragment>
                {before}
                <b>{program}</b>
                {after}
            </React.Fragment>
        );
    };

    renderHeader = (student, highlights, grade) => (
        <div className="iitf-section1 fdn-hero fdn-hero--violet fdn-pop">
            <span className="iitf-s1__confetti" aria-hidden="true">
                <i /><i /><i /><i />
            </span>
            <div className="iitf-s1__left">
                <div className="iitf-s1__identity">
                    <div className="iitf-s1__avatar" title={student.name}>
                        {student.avatar && !this.state.photoFailed ? (
                            <img
                                src={student.avatar}
                                alt={student.name}
                                onError={() => this.setState({ photoFailed: true })}
                            />
                        ) : (
                            <div className="iitf-s1__initials">{this.getInitials(student.name)}</div>
                        )}
                    </div>

                    <div className="iitf-s1__intro">
                        <h1 className="iitf-s1__greeting">
                            {this.getGreeting()}, {student.firstName || student.name}
                            <span className="iitf-s1__wave" role="img" aria-label="waving hand">
                                👋
                            </span>
                        </h1>
                        <p className="iitf-s1__tagline">
                            {this.renderTagline(student.tagline || "", student.program)}
                        </p>
                    </div>
                </div>

                <div className="iitf-s1__tiles">
                    {highlights.map((item) => (
                        <div
                            className={`iitf-s1__tile ${item.accent ? "is-accent" : ""} ${item.emoji ? "has-emoji" : ""
                                }`}
                            key={item.id}
                        >
                            {item.emoji ? <span className="iitf-s1__tile-emoji">{item.emoji}</span> : null}
                            <div>
                                <small>{item.label}</small>
                                <strong>
                                    {item.value}
                                    {item.hint ? <em>{item.hint}</em> : null}
                                </strong>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {grade ? (
                <div className="iitf-s1__grade">
                    <span className="iitf-s1__grade-medal" aria-hidden="true">🏅</span>
                    <div className="iitf-s1__grade-label">{grade.label}</div>
                    <div className="iitf-s1__grade-value">{grade.grade}</div>
                    <div className="iitf-s1__grade-meta">
                        {grade.percentage}%
                    </div>
                    {grade.delta ? (
                        <div className="iitf-s1__grade-delta">
                            <i className={`fas fa-arrow-${grade.delta >= 0 ? "up" : "down"}`} />
                            {Math.abs(grade.delta)}% this month
                        </div>
                    ) : null}
                    {grade.hint ? <div className="iitf-s1__grade-hint">{grade.hint}</div> : null}
                </div>
            ) : null}
        </div>
    );

    /* ------------------------------------------------------ quick actions */

    renderQuickActions = (quickActions) => {
        if (!quickActions || !quickActions.cards) return null;

        const body = this.isLoading()
            ? this.renderSkeleton(3, "cols-3 tall")
            : (
                <div className="iitf-quick__grid">
                    {quickActions.cards.map((card) => (
                        <article className={`iitf-quick__card fdn-card fdn-card--hover theme-${card.theme}`} key={card.id}>
                            <div className="iitf-quick__top">
                                <span className="iitf-quick__icon">
                                    <i className={card.icon} />
                                </span>
                                {card.badge ? (
                                    <span className={`iitf-quick__badge tone-${card.badgeTone || "soft"}`}>
                                        {card.badge}
                                    </span>
                                ) : null}
                            </div>

                            <h5 className="iitf-quick__title">{card.title}</h5>
                            <p className="iitf-quick__desc">{card.description}</p>

                            <div className="iitf-quick__metric">
                                <strong>{card.metricValue}</strong>
                                <span>{card.metricLabel}</span>
                            </div>

                            {card.hideProgress ? null : (
                                <div className="iitf-quick__progress">
                                    <div className="track">
                                        <span style={{ width: `${this.state.mounted ? card.progress : 0}%` }} />
                                    </div>
                                    <div className="meta">
                                        <span>{card.progressLabel}</span>
                                        <b>{card.progress}%</b>
                                    </div>
                                </div>
                            )}

                            <button
                                type="button"
                                className="iitf-quick__cta fdn-btn fdn-btn--block"
                                onClick={() => this.emit(card.id)}
                            >
                                {card.cta}
                                <i className="fas fa-arrow-right" />
                            </button>
                        </article>
                    ))}
                </div>
            );

        return this.renderSection(
            {
                id: "quick-actions",
                title: quickActions.title,
                subtitle: quickActions.subtitle,
                actionLabel: quickActions.actionLabel,
                actionIcon: "fas fa-sliders-h",
                actionKey: "edit-shortcuts",
                className: "iitf-quick",
            },
            body
        );
    };

    /* --------------------------------------------------------- KPI strip */

    renderKpis = (kpis) => {
        if (!kpis || !kpis.cards) return null;

        const body = this.isLoading()
            ? this.renderSkeleton(6, "cols-6")
            : (
                <div className="iitf-kpi__grid">
                    {kpis.cards.map((card) => (
                        <article className={`iitf-kpi fdn-card--hover tone-${card.tone}`} key={card.id}>
                            <div className="iitf-kpi__top">
                                <span className="iitf-kpi__icon">
                                    <i className={card.icon} />
                                </span>
                                <span className={`iitf-kpi__delta ${card.delta >= 0 ? "is-up" : "is-down"}`}>
                                    <i className={`fas fa-arrow-${card.delta >= 0 ? "up" : "down"}`} />
                                    {Math.abs(card.delta)}
                                </span>
                            </div>

                            <div className="iitf-kpi__value">
                                <CountUp
                                    value={card.value}
                                    decimals={card.decimals || 0}
                                    prefix={card.prefix || ""}
                                    suffix={card.suffix || ""}
                                />
                            </div>
                            <div className="iitf-kpi__label">{card.label}</div>

                            <div className="iitf-kpi__bar">
                                <span style={{ width: `${this.state.mounted ? card.progress : 0}%` }} />
                            </div>
                            <div className="iitf-kpi__caption">{card.caption}</div>
                        </article>
                    ))}
                </div>
            );

        return this.renderSection(
            { id: "kpis", title: kpis.title, subtitle: kpis.subtitle, className: "iitf-kpis" },
            body
        );
    };

    /* ------------------------------------------- weekly + subject mastery */

    weeklyChart = (trend) => ({
        options: {
            chart: {
                type: "line",
                height: 190,
                toolbar: { show: false },
                fontFamily: "inherit",
                foreColor: CHART_FG,
                animations: { enabled: true, easing: "easeinout", speed: 800 },
            },
            colors: [BAND_COLOR.good, BAND_COLOR.excellent],
            stroke: { width: [0, 3], curve: "smooth" },
            plotOptions: { bar: { borderRadius: 4, columnWidth: "42%" } },
            markers: { size: [0, 4], hover: { size: 6 } },
            grid: { borderColor: CHART_GRID, strokeDashArray: 4, padding: { left: 2, right: 2 } },
            dataLabels: { enabled: false },
            legend: { position: "top", horizontalAlign: "right", fontSize: "11px", markers: { radius: 12 } },
            xaxis: { categories: trend.days, axisBorder: { show: false }, axisTicks: { show: false } },
            yaxis: [
                { labels: { style: { fontSize: "10px" } } },
                {
                    opposite: true,
                    min: 50,
                    max: 100,
                    labels: { formatter: (v) => `${Math.round(v)}%`, style: { fontSize: "10px" } },
                },
            ],
            // `shared` requires `intersect:false` or ApexCharts throws
            tooltip: { shared: true, intersect: false, theme: "light" },
        },
        series: [
            { name: "Questions", type: "column", data: trend.questions },
            { name: "Accuracy", type: "line", data: trend.accuracy },
        ],
    });

    renderInsights = (weekly, mastery) => {
        if (!weekly && !mastery) return null;

        const single = !weekly || !mastery;
        const body = this.isLoading()
            ? this.renderSkeleton(2, "cols-2 tall")
            : (
                <div className={`iitf-pi__grid ${single ? "is-single" : ""}`}>
                    {weekly ? this.renderWeeklyCard(weekly) : null}
                    {mastery ? this.renderMasteryCard(mastery) : null}
                </div>
            );

        return this.renderSection(
            {
                id: "insights",
                title: "Performance Insights",
                subtitle: weekly ? "How this week went, and where each subject stands" : "Where each subject stands",
                className: "iitf-insights",
            },
            body
        );
    };

    renderWeeklyCard = (weekly) => {
        const chart = this.weeklyChart(weekly.trend);

        return (
            <article className="iitf-pi__card fdn-card is-weekly">
                <header className="iitf-pi__card-head">
                    <span className="iitf-pi__card-icon">
                        <i className="fas fa-chart-line" />
                    </span>
                    <div>
                        <h5>{weekly.title}</h5>
                        <small>{weekly.subtitle}</small>
                    </div>
                    {weekly.badge ? <span className="iitf-pi__chip">{weekly.badge}</span> : null}
                </header>

                <div className="iitf-pi__kpis">
                    {weekly.kpis.map((kpi) => (
                        <div className={`iitf-pi__kpi tone-${kpi.tone}`} key={kpi.id}>
                            <i className={kpi.icon} />
                            <strong>
                                <CountUp value={kpi.value} decimals={kpi.decimals || 0} suffix={kpi.suffix || ""} />
                            </strong>
                            <small>{kpi.label}</small>
                            {kpi.delta ? <em>+{kpi.delta}</em> : null}
                        </div>
                    ))}
                </div>

                <div className="iitf-pi__chart">
                    <ReactApexChart options={chart.options} series={chart.series} type="line" height={190} />
                </div>
            </article>
        );
    };

    renderMasteryCard = (mastery) => (
        <article className="iitf-pi__card fdn-card is-mastery">
            <header className="iitf-pi__card-head">
                <span className="iitf-pi__card-icon">
                    <i className="fas fa-graduation-cap" />
                </span>
                <div>
                    <h5>{mastery.title}</h5>
                    <small>{mastery.subtitle}</small>
                </div>
                {mastery.badge ? <span className="iitf-pi__chip">{mastery.badge}</span> : null}
            </header>

            <div className="iitf-pi__rows">
                {mastery.subjects.map((subject) => (
                    <div className={`iitf-pi__row ${subjClass(subject.subject)} band-${bandFor(subject.mastery)}`} key={subject.id}>
                        <div className="iitf-pi__row-top">
                            <span className="iitf-pi__row-icon">
                                <i className={subject.icon} />
                            </span>
                            <span className="iitf-pi__row-name">{subject.subject}</span>
                            <span className="iitf-pi__row-mastery">{subject.mastery}%</span>
                        </div>

                        <div className="iitf-pi__row-stats">
                            <span>
                                <i className="fas fa-pen" />
                                <b>{subject.questionsSolved}</b> solved
                            </span>
                            <span>
                                <i className="fas fa-stopwatch" />
                                <b>{subject.avgTimePerQuestion}s</b> / question
                            </span>
                            <span>
                                <i className="fas fa-bullseye" />
                                <b>{subject.accuracy}%</b> accuracy
                            </span>
                        </div>

                        <div className="iitf-pi__bar">
                            <span style={{ width: `${this.state.mounted ? subject.mastery : 0}%` }} />
                        </div>
                    </div>
                ))}
            </div>

            <div className="iitf-pi__legend">
                <span>
                    <i style={{ background: BAND_COLOR.excellent }} /> Mastered 85%+
                </span>
                <span>
                    <i style={{ background: BAND_COLOR.good }} /> Good 70-84%
                </span>
                <span>
                    <i style={{ background: BAND_COLOR.average }} /> Average 55-69%
                </span>
                <span>
                    <i style={{ background: BAND_COLOR.poor }} /> Needs work
                </span>
            </div>
        </article>
    );

    /* ------------------------------------------------------ exam analysis */

    renderExamAnalysis = (examAnalysis) => {
        if (!examAnalysis || !examAnalysis.exams) return null;

        const body = this.isLoading()
            ? this.renderSkeleton(4, "rows")
            : (
                <div className="iitf-exam__wrap fdn-card">
                    <table className="iitf-exam__table">
                        <thead>
                            <tr>
                                {examAnalysis.columns.map((column) => (
                                    <th key={column}>{column}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {examAnalysis.exams.map((exam) => {
                                const band = bandFor(exam.percentage);
                                return (
                                    <tr key={exam.id}>
                                        <td data-label="Exam">
                                            <span className="name">{exam.name}</span>
                                            <small>{exam.type}</small>
                                        </td>
                                        <td data-label="Date">{exam.date}</td>
                                        <td data-label="Score">
                                            <b>{exam.score}</b>/{exam.total}
                                        </td>
                                        <td data-label="Accuracy">
                                            <div className="iitf-exam__bar">
                                                <span
                                                    style={{
                                                        width: `${this.state.mounted ? exam.accuracy : 0}%`,
                                                        background: BAND_COLOR[bandFor(exam.accuracy)],
                                                    }}
                                                />
                                            </div>
                                            <small>{exam.accuracy}%</small>
                                        </td>
                                        <td data-label="Rank">
                                            #{exam.rank}
                                            <small> of {exam.outOf.toLocaleString("en-IN")}</small>
                                        </td>
                                        <td data-label="Result">
                                            <span className={`iitf-pill band-${band}`}>{exam.percentage}%</span>
                                        </td>
                                        <td data-label="">
                                            <button
                                                type="button"
                                                className="iitf-view-btn fdn-btn fdn-btn--secondary fdn-btn--sm"
                                                onClick={() => this.props.onViewAnalysis ? this.props.onViewAnalysis(exam) : this.openAnalysis(exam)}
                                            >
                                                <i className="fas fa-chart-pie" />
                                                View Analysis
                                            </button>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            );

        return this.renderSection(
            {
                id: "exams",
                title: examAnalysis.title,
                subtitle: examAnalysis.subtitle,
                badge: examAnalysis.badge,
                className: "iitf-exam",
            },
            body
        );
    };

    /* ============================================================
       Exam Analysis — student view (Class 6-10)
       Opened by "View Analysis"; every value comes from the data file.
       ============================================================ */

    openAnalysis = (exam) => this.setState({ analysisExam: exam, questionFilter: "all" });

    closeAnalysis = () => this.setState({ analysisExam: null });

    getAnalysis = (exam) => {
        const detail = (this.props.data || section1DummyData).examAnalysisDetail || {};
        return detail[exam.id] || detail.default || null;
    };

    /** Circular indicator drawn with a conic gradient — no chart library. */
    renderRing = (value, color, caption, sub) => (
        <div className="iitf-ring">
            <div
                className="iitf-ring__circle"
                style={{
                    background: `conic-gradient(${color} ${(this.state.mounted ? value : 0) * 3.6}deg, ${CHART_TRACK} 0deg)`,
                }}
            >
                <div className="iitf-ring__hole">
                    <strong>{value}%</strong>
                    <small>{caption}</small>
                </div>
            </div>
            {sub ? <span className="iitf-ring__sub">{sub}</span> : null}
        </div>
    );

    /** Excellent / Good / Average / Needs Improvement badge. */
    renderBadge = (value) => {
        const band = bandFor(value);
        const label = {
            excellent: "Excellent",
            good: "Good",
            average: "Average",
            poor: "Needs Improvement",
        }[band];
        return <span className={`iitf-pill band-${band}`}>{label}</span>;
    };

    /* ---------- simple, colourful charts for the analysis modal ---------- */

    /** Shared base: no toolbar, soft grid, student-friendly sizing. */
    simpleChart = (overrides) => ({
        chart: {
            toolbar: { show: false },
            fontFamily: "inherit",
            foreColor: CHART_FG,
            animations: { enabled: true, easing: "easeinout", speed: 800 },
            ...(overrides.chart || {}),
        },
        grid: { borderColor: CHART_GRID, strokeDashArray: 4 },
        legend: { show: false },
        tooltip: { theme: "light", shared: false, intersect: true },
        ...Object.keys(overrides).reduce((acc, key) => {
            if (key !== "chart") acc[key] = overrides[key];
            return acc;
        }, {}),
    });

    subjectChart = (subjects) => ({
        options: this.simpleChart({
            chart: { type: "bar", height: 210 },
            plotOptions: { bar: { borderRadius: 8, columnWidth: "48%", distributed: true } },
            colors: subjects.map((subject) => BAND_COLOR[bandFor(subject.accuracy)]),
            xaxis: {
                categories: subjects.map((subject) => subject.subject),
                axisBorder: { show: false },
                axisTicks: { show: false },
                labels: { style: { fontSize: "11px", fontWeight: 600 } },
            },
            yaxis: { max: 100, labels: { formatter: (v) => `${Math.round(v)}%`, style: { fontSize: "10px" } } },
            dataLabels: {
                enabled: true,
                formatter: (v) => `${v}%`,
                offsetY: -20,
                style: { fontSize: "11px", fontWeight: 700, colors: ["#4a4867"] },
            },
        }),
        series: [{ name: "Accuracy", data: subjects.map((subject) => subject.accuracy) }],
    });

    chapterChart = (chapters) => ({
        options: this.simpleChart({
            chart: { type: "bar", height: Math.max(200, chapters.length * 34) },
            plotOptions: { bar: { borderRadius: 6, horizontal: true, barHeight: "62%", distributed: true } },
            colors: chapters.map((chapter) => BAND_COLOR[bandFor(chapter.accuracy)]),
            xaxis: {
                categories: chapters.map((chapter) => chapter.chapter),
                max: 100,
                labels: { formatter: (v) => `${Math.round(v)}%`, style: { fontSize: "10px" } },
            },
            yaxis: { labels: { style: { fontSize: "11px" } } },
            dataLabels: {
                enabled: true,
                formatter: (v) => `${Math.round(v)}%`,
                style: { fontSize: "10.5px", fontWeight: 700, colors: ["#fff"] },
            },
        }),
        series: [{ name: "Accuracy", data: chapters.map((chapter) => Math.round(chapter.accuracy)) }],
    });

    /** Treemap: each topic is a tile sized by questions solved, coloured by
        how well it went — easier to read than eight overlapping rings. */
    topicChart = (topics) => ({
        options: this.simpleChart({
            chart: { type: "treemap", height: 260 },
            plotOptions: {
                treemap: {
                    distributed: false,
                    enableShades: false,
                    colorScale: {
                        ranges: [
                            { from: 0, to: 54.9, color: BAND_COLOR.poor, name: "Practice More" },
                            { from: 55, to: 69.9, color: BAND_COLOR.average, name: "Keep Going" },
                            { from: 70, to: 84.9, color: BAND_COLOR.good, name: "Good" },
                            { from: 85, to: 100, color: BAND_COLOR.excellent, name: "Strong" },
                        ],
                    },
                },
            },
            dataLabels: {
                enabled: true,
                style: { fontSize: "12px", fontWeight: 700 },
                formatter: (text, opts) => [text, `${opts.value}%`],
                offsetY: -3,
            },
            tooltip: {
                theme: "light",
                y: { formatter: (value) => `${value}% correct` },
            },
            legend: { show: false },
        }),
        series: [
            {
                data: topics.map((topic) => ({
                    x: topic.topic,
                    y: Math.round(topic.accuracy),
                })),
            },
        ],
    });

    /** Question-by-question review with options, in student-friendly language. */
    renderQuestions = (questions) => {
        if (!questions || !questions.length) return null;

        // Only questions the student actually answered ("written"); skip unattempted.
        const answered = questions.filter((q) => q.status !== "unattempted");
        if (!answered.length) return null;

        const filters = [
            { key: "all", emoji: "📋", label: "All" },
            { key: "correct", emoji: "✅", label: "Correct" },
            { key: "wrong", emoji: "❌", label: "Wrong" },
        ];

        const active = this.state.questionFilter === "unattempted" ? "all" : this.state.questionFilter;
        const visible = active === "all" ? answered : answered.filter((q) => q.status === active);

        const statusLabel = { correct: "Correct", wrong: "Wrong", unattempted: "Not answered" };

        return (
            <div className="iitf-block">
                <h5 className="iitf-block__title">
                    <span className="dot tone-blue">❓</span>Questions &amp; Answers
                </h5>

                <div className="iitf-qfilters">
                    {filters.map((filter) => {
                        const count =
                            filter.key === "all"
                                ? answered.length
                                : answered.filter((q) => q.status === filter.key).length;
                        return (
                            <button
                                type="button"
                                key={filter.key}
                                className={`iitf-qfilter fdn-chip is-${filter.key} ${active === filter.key ? "is-active" : ""}`}
                                onClick={() => this.setState({ questionFilter: filter.key })}
                            >
                                <span>{filter.emoji}</span>
                                {filter.label}
                                <em>{count}</em>
                            </button>
                        );
                    })}
                </div>

                <div className="iitf-questions">
                    {visible.length ? (
                        visible.map((question) => (
                            <article className={`iitf-question is-${question.status}`} key={question.id}>
                                <header>
                                    <span className="iitf-question__no">Q{question.no}</span>
                                    <p>{question.question}</p>
                                    <div className="iitf-question__side">
                                        <span className={`iitf-qstatus is-${question.status}`}>
                                            {statusLabel[question.status]}
                                        </span>
                                        <span className="iitf-question__marks">
                                            {question.marks > 0 ? `+${question.marks}` : question.marks}
                                        </span>
                                    </div>
                                </header>

                                <div className="iitf-question__tags">
                                    <span>{question.subject}</span>
                                    <span>{question.chapter}</span>
                                    <span>{question.topic}</span>
                                    {question.timeTaken ? <span>⏱️ {question.timeTaken}s</span> : null}
                                </div>

                                <div className="iitf-options">
                                    {question.options.map((option) => {
                                        const isCorrect = option.key === question.correctAnswer;
                                        const isChosen = option.key === question.studentAnswer;
                                        return (
                                            <div
                                                className={`iitf-option ${isCorrect ? "is-correct" : ""} ${isChosen && !isCorrect ? "is-wrong" : ""
                                                    }`}
                                                key={option.key}
                                            >
                                                <span className="iitf-option__key">{option.key}</span>
                                                <span className="iitf-option__text">{option.text}</span>
                                                {isCorrect ? <em className="tick">✅ Correct answer</em> : null}
                                                {isChosen && !isCorrect ? <em className="cross">❌ Your answer</em> : null}
                                                {isChosen && isCorrect ? <em className="tick">👍 You picked this</em> : null}
                                            </div>
                                        );
                                    })}
                                </div>

                                <div className="iitf-why">
                                    <strong>💡 Why?</strong>
                                    <p>{question.explanation}</p>
                                </div>
                            </article>
                        ))
                    ) : (
                        <div className="iitf-empty fdn-empty">
                            <div className="fdn-empty__icon">🎉</div>
                            No questions in this group.
                        </div>
                    )}
                </div>
            </div>
        );
    };

    renderAnalysisModal = () => {
        const exam = this.state.analysisExam;
        if (!exam) return null;

        const analysis = this.getAnalysis(exam);
        if (!analysis) return null;

        const o = analysis.overall;

        return (
            <Modal
                show
                onHide={this.closeAnalysis}
                className="fdn-theme iitf-modal-root"
                dialogClassName="iitf-modal"
                aria-labelledby="iitf-modal-title"
                scrollable
            >
                {/* ---------------------------------------------- header */}
                <div className="iitf-modal__head">
                    <div>
                        <span className="iitf-modal__eyebrow">{exam.type} • {exam.date}</span>
                        <h3 id="iitf-modal-title">{exam.name}</h3>
                    </div>
                    <button type="button" className="iitf-modal__close" onClick={this.closeAnalysis}>
                        <i className="fas fa-times" />
                    </button>
                </div>

                <div className="iitf-modal__body">
                    {/* ------------------------------------- motivation */}
                    <div className={`iitf-motivate tone-${analysis.motivation.tone}`}>
                        <span className="emoji">{analysis.motivation.emoji}</span>
                        <div>
                            <h4>{analysis.motivation.title}</h4>
                            <p>{analysis.motivation.message}</p>
                        </div>
                    </div>

                    {/* --------------------------------- 1. overall */}
                    <div className="iitf-block">
                        <h5 className="iitf-block__title">
                            <span className="dot tone-blue">📊</span>Overall Performance
                        </h5>

                        <div className="iitf-overall">
                            <div className="iitf-overall__hero">
                                {this.renderRing(o.percentage, BAND_COLOR[bandFor(o.percentage)], "score")}
                                <div className="iitf-overall__score">
                                    <strong>
                                        <CountUp value={o.totalMarks} />
                                        <em>/{o.maxMarks}</em>
                                    </strong>
                                    <span className={`iitf-grade band-${bandFor(o.percentage)}`}>
                                        Grade {o.grade}
                                    </span>
                                    <small>
                                        Rank #{o.rank} of {o.outOf.toLocaleString("en-IN")}
                                    </small>
                                </div>
                            </div>

                            <div className="iitf-stats">
                                <div className="iitf-stat tone-blue">
                                    <span className="emoji">📝</span>
                                    <strong><CountUp value={o.attempted} /></strong>
                                    <small>Attempted</small>
                                </div>
                                <div className="iitf-stat tone-green">
                                    <span className="emoji">✅</span>
                                    <strong><CountUp value={o.correct} /></strong>
                                    <small>Correct</small>
                                </div>
                                <div className="iitf-stat tone-red">
                                    <span className="emoji">❌</span>
                                    <strong><CountUp value={o.wrong} /></strong>
                                    <small>Wrong</small>
                                </div>
                                <div className="iitf-stat tone-orange">
                                    <span className="emoji">⬜</span>
                                    <strong><CountUp value={o.unattempted} /></strong>
                                    <small>Not answered</small>
                                </div>
                                <div className="iitf-stat tone-purple">
                                    <span className="emoji">🎯</span>
                                    <strong><CountUp value={o.accuracy} decimals={1} suffix="%" /></strong>
                                    <small>Accuracy</small>
                                </div>
                                <div className="iitf-stat tone-teal">
                                    <span className="emoji">⏱️</span>
                                    <strong>{o.timeTaken}</strong>
                                    <small>of {o.totalTime}</small>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* --- subjects / chapters / topics share one 3-column row --- */}
                    <div className="iitf-three">
                    {/* --------------------------------- 2. subjects */}
                    <div className="iitf-block">
                        <h5 className="iitf-block__title">
                            <span className="dot tone-purple">📚</span>Subject Performance
                        </h5>

                        <div className="iitf-chartbox">
                            <ReactApexChart
                                options={this.subjectChart(analysis.subjects).options}
                                series={this.subjectChart(analysis.subjects).series}
                                type="bar"
                                height={210}
                            />
                        </div>
                    </div>

                    {/* --------------------------------- 3. chapters */}
                    <div className="iitf-block">
                        <h5 className="iitf-block__title">
                            <span className="dot tone-orange">📖</span>Chapter Performance
                        </h5>

                        <div className="iitf-chartbox">
                            <ReactApexChart
                                options={this.chapterChart(analysis.chapters).options}
                                series={this.chapterChart(analysis.chapters).series}
                                type="bar"
                                height={Math.max(200, analysis.chapters.length * 34)}
                            />
                        </div>
                    </div>

                    {/* --------------------------------- 4. topics */}
                    <div className="iitf-block">
                        <h5 className="iitf-block__title">
                            <span className="dot tone-teal">🧩</span>Topic Performance
                        </h5>

                        <div className="iitf-chartbox">
                            <ReactApexChart
                                options={this.topicChart(analysis.topics).options}
                                series={this.topicChart(analysis.topics).series}
                                type="treemap"
                                height={260}
                            />
                        </div>
                    </div>

                    </div>

                    {/* --------------------------------- questions & answers */}
                    {this.renderQuestions(analysis.questions)}
                </div>

                {/* ---------------------------------------------- footer */}
                <div className="iitf-modal__foot">
                    <button type="button" className="iitf-modal__ghost fdn-btn fdn-btn--secondary" onClick={this.closeAnalysis}>
                        Close
                    </button>
                </div>
            </Modal>
        );
    };

    renderPlannerAndLeaderboard = (planner, leaderboard) => {
        if (!planner && !leaderboard) return null;

        const body = this.isLoading()
            ? this.renderSkeleton(2, "cols-2 tall")
            : (
                <div className="iitf-split__grid">
                    {planner ? (
                        <article className="iitf-panel fdn-card">
                            <header className="iitf-panel__head">
                                <span className="iitf-panel__icon is-planner">
                                    <i className="fas fa-calendar-check" />
                                </span>
                                <div>
                                    <h5>{planner.title}</h5>
                                    <small>{planner.subtitle}</small>
                                </div>
                                {planner.badge ? <span className="iitf-pi__chip">{planner.badge}</span> : null}
                            </header>

                            <div className="iitf-plan">
                                {planner.tasks.map((task) => (
                                    <div
                                        className={`iitf-plan__item priority-${task.priority} ${task.done ? "is-done" : ""
                                            }`}
                                        key={task.id}
                                    >
                                        <div className="iitf-plan__day">
                                            <strong>{task.day}</strong>
                                            <small>{task.date}</small>
                                        </div>
                                        <div className="iitf-plan__main">
                                            <h6>{task.task}</h6>
                                            <p>
                                                {task.subject} • {task.duration} • {task.priority}
                                            </p>
                                        </div>
                                        <span className={`iitf-pill ${task.done ? "band-excellent" : "band-average"}`}>
                                            <i className={`fas fa-${task.done ? "check" : "clock"}`} />
                                            {task.done ? "Done" : "Due"}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        </article>
                    ) : null}

                    {leaderboard ? (
                        <article className="iitf-panel fdn-card">
                            <header className="iitf-panel__head">
                                <span className="iitf-panel__icon is-leader">
                                    <i className="fas fa-trophy" />
                                </span>
                                <div>
                                    <h5>{leaderboard.title}</h5>
                                    <small>{leaderboard.subtitle}</small>
                                </div>
                                {leaderboard.badge ? (
                                    <span className="iitf-pi__chip">{leaderboard.badge}</span>
                                ) : null}
                            </header>

                            <div className="iitf-board">
                                {leaderboard.students.map((row) => (
                                    <div className={`iitf-board__row ${row.isYou ? "is-you" : ""}`} key={row.id}>
                                        <span className={`iitf-board__rank rank-${row.rank}`}>{row.rank}</span>
                                        <img src={row.avatar} alt={row.name} />
                                        <div className="iitf-board__main">
                                            <h6>
                                                {row.name}
                                                {row.isYou ? <em>You</em> : null}
                                            </h6>
                                            <div className="iitf-board__bar">
                                                <span style={{ width: `${this.state.mounted ? row.score : 0}%` }} />
                                            </div>
                                        </div>
                                        <div className="iitf-board__side">
                                            <strong>{row.score}%</strong>
                                            {row.delta ? (
                                                <span className={row.delta >= 0 ? "is-up" : "is-down"}>
                                                    <i className={`fas fa-arrow-${row.delta >= 0 ? "up" : "down"}`} />
                                                    {Math.abs(row.delta)}
                                                </span>
                                            ) : (
                                                <span className="is-flat">—</span>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </article>
                    ) : null}
                </div>
            );

        return this.renderSection(
            {
                id: "planner-board",
                title: "Planner & Leaderboard",
                subtitle: "This week's plan and where you stand in the batch",
                className: "iitf-split",
            },
            body
        );
    };

    /* ---------------------------------------------------------- attendance */

    attendanceChart = (attendance) => ({
        options: {
            chart: {
                type: "area",
                height: 150,
                toolbar: { show: false },
                fontFamily: "inherit",
                foreColor: CHART_FG,
                sparkline: { enabled: false },
            },
            colors: [BAND_COLOR.excellent],
            stroke: { curve: "smooth", width: 3 },
            fill: {
                type: "gradient",
                gradient: { shadeIntensity: 1, opacityFrom: 0.35, opacityTo: 0.02, stops: [0, 95] },
            },
            markers: { size: 4, hover: { size: 6 } },
            grid: { borderColor: CHART_GRID, strokeDashArray: 4 },
            dataLabels: { enabled: false },
            legend: { show: false },
            xaxis: {
                categories: attendance.months,
                axisBorder: { show: false },
                axisTicks: { show: false },
                labels: { style: { fontSize: "10px" } },
            },
            yaxis: { min: 70, max: 100, labels: { formatter: (v) => `${Math.round(v)}%`, style: { fontSize: "10px" } } },
            tooltip: { theme: "light", y: { formatter: (v) => `${v}% present` } },
        },
        series: [{ name: "Attendance", data: attendance.monthly }],
    });

    renderAttendance = (attendance) => {
        if (!attendance) return null;

        const chart = this.attendanceChart(attendance);
        const ring = this.state.mounted ? attendance.percentage : 0;

        const body = this.isLoading()
            ? this.renderSkeleton(3, "cols-3")
            : (
                <div className="iitf-att__grid">
                    <div className="iitf-att__ring">
                        <div
                            className="ring"
                            style={{
                                background: `conic-gradient(${BAND_COLOR.excellent} ${ring * 3.6}deg, ${CHART_TRACK} ${ring * 3.6}deg)`,
                            }}
                        >
                            <div className="hole">
                                <strong>
                                    <CountUp value={attendance.percentage} decimals={1} suffix="%" />
                                </strong>
                                <small>present</small>
                            </div>
                        </div>
                        <div className="iitf-att__stats">
                            <div>
                                <strong>{attendance.present}</strong>
                                <small>Present</small>
                            </div>
                            <div>
                                <strong>{attendance.absent}</strong>
                                <small>Absent</small>
                            </div>
                            <div>
                                <strong>{attendance.lateArrivals}</strong>
                                <small>Late</small>
                            </div>
                        </div>
                    </div>

                    <div className="iitf-att__chart">
                        <ReactApexChart options={chart.options} series={chart.series} type="area" height={150} />
                    </div>

                    <div className="iitf-att__subjects">
                        {attendance.bySubject.map((row) => {
                            const pct = Math.round((row.attended / row.total) * 100);
                            return (
                                <div className="iitf-att__row" key={row.id}>
                                    <div className="top">
                                        <span>{row.subject}</span>
                                        <b style={{ color: BAND_COLOR[bandFor(pct)] }}>{pct}%</b>
                                    </div>
                                    <div className="iitf-att__bar">
                                        <span
                                            style={{
                                                width: `${this.state.mounted ? pct : 0}%`,
                                                background: BAND_COLOR[bandFor(pct)],
                                            }}
                                        />
                                    </div>
                                    <small>
                                        {row.attended} of {row.total} sessions
                                    </small>
                                </div>
                            );
                        })}
                    </div>
                </div>
            );

        return this.renderSection(
            {
                id: "attendance",
                title: attendance.title,
                subtitle: attendance.subtitle,
                badge: attendance.badge,
                className: "iitf-att",
            },
            body
        );
    };

    /* ---------------------------------------------------------------- render */

    render() {
        const data = this.props.data || section1DummyData;
        // Real logged-in student (from login/registration) overrides the dummy
        // header identity; charts stay on dummy data until a data layer exists.
        const student = { ...(data.student || {}), ...(this.props.student || {}) };

        return (
            <Container fluid className="iitf-container px-0">
                <div className={`iitf-dashboard ${this.isLoading() ? "is-loading" : ""}`}>
                    {this.renderHeader(student, data.highlights || [], data.grade)}
                    {this.renderQuickActions(data.quickActions)}
                    {this.renderKpis(data.kpis)}
                    {this.renderInsights(data.weekly, data.mastery)}
                    {this.renderExamAnalysis(data.examAnalysis)}
                    {this.renderAnalysisModal()}
                    {/* {this.renderPlannerAndLeaderboard(data.planner, data.leaderboard)} */}
                    {/* {this.renderAttendance(data.attendance)} */}
                </div>
            </Container>
        );
    }
}

export default IITFoundationStudentDashboardSection1;
